import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Share } from '@capacitor/share';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { PublicResourceUrlService, ShareableResourceType } from '../services/public-resource-url.service';
import { blobToBase64 } from '../../utils/encoding/encoding';

export interface QrDialogData {
  resourceType: ShareableResourceType;
  title: string;
  url: string;
  subtitle?: string | null;
}

@Component({
  selector: 'app-qr-dialog',
  standalone: true,
  imports: [
    NgIf,
    ButtonModule,
    DialogModule,
    TranslateModule
  ],
  templateUrl: './qr-dialog.component.html',
  styleUrl: './qr-dialog.component.scss'
})
export class QrDialogComponent {
  @Input() public visible: boolean = false;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() public data: QrDialogData | null = null;

  constructor(
    private readonly translate: TranslateService,
    private readonly publicResourceUrlService: PublicResourceUrlService
  ) {
  }

  get qrImageUrl(): string | null {
    if (!this.data?.url) {
      return null;
    }

    return this.publicResourceUrlService.getQrCodeImageUrl(this.data.url);
  }

  public hide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  public async shareLink(): Promise<void> {
    if (!this.data?.url) {
      return;
    }

    await Share.share({
      title: this.data.title,
      text: this.data.subtitle || this.data.title,
      url: this.data.url,
      dialogTitle: this.translate.instant('common.share'),
    });
  }

  public async shareQr(): Promise<void> {
    if (!this.data?.url || !this.qrImageUrl) {
      return;
    }

    const qrCardBlob = await this.buildQrCardBlob();
    const fileName = this.buildQrFileName();
    const file = new File([qrCardBlob], fileName, {type: 'image/png'});

    try {
      if (navigator.canShare?.({files: [file]})) {
        await navigator.share({
          files: [file],
          title: this.data.title,
          text: this.data.subtitle || this.data.title
        });
        return;
      }
    } catch {
      // Fallback to Capacitor share or direct download below.
    }

    try {
      const path = `shared-qr/${Date.now()}-${fileName}`;
      await Filesystem.writeFile({
        path,
        data: await blobToBase64(qrCardBlob),
        directory: Directory.Cache,
        recursive: true
      });
      const fileUri = await Filesystem.getUri({
        path,
        directory: Directory.Cache
      });

      await Share.share({
        title: this.data.title,
        text: this.data.subtitle || this.data.title,
        files: [fileUri.uri],
        dialogTitle: this.translate.instant('shared.qr.dialog.share_qr')
      });
      return;
    } catch {
      this.downloadBlob(qrCardBlob, fileName);
    }
  }

  private async buildQrCardBlob(): Promise<Blob> {
    const qrResponse = await fetch(this.qrImageUrl);
    const qrBlob = await qrResponse.blob();
    const qrObjectUrl = URL.createObjectURL(qrBlob);

    try {
      const qrImage = await this.loadImage(qrObjectUrl);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Canvas context unavailable');
      }

      const size = 1200;
      const padding = 64;
      const qrSize = 860;
      const cardX = 80;
      const cardY = 80;
      const cardWidth = size - (cardX * 2);
      const cardHeight = size - (cardY * 2);
      const qrX = Math.round((size - qrSize) / 2);
      const qrY = 150;

      canvas.width = size;
      canvas.height = size;

      context.fillStyle = '#eef2f7';
      context.fillRect(0, 0, size, size);

      context.fillStyle = '#ffffff';
      context.shadowColor = 'rgba(15, 23, 42, 0.12)';
      context.shadowBlur = 36;
      context.shadowOffsetY = 18;
      this.drawRoundedRect(context, cardX, cardY, cardWidth, cardHeight, 40);
      context.fill();

      context.shadowColor = 'transparent';
      context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

      context.fillStyle = '#111827';
      context.font = '600 52px Arial, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(this.truncateLabel(this.data?.title || ''), size / 2, 1060, size - (padding * 2));

      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('QR card export failed'));
            return;
          }
          resolve(blob);
        }, 'image/png');
      });
    } finally {
      URL.revokeObjectURL(qrObjectUrl);
    }
  }

  private async loadImage(src: string): Promise<HTMLImageElement> {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  private drawRoundedRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
  }

  private truncateLabel(label: string): string {
    return label.length > 36 ? `${label.slice(0, 33)}...` : label;
  }

  private buildQrFileName(): string {
    const safeTitle = (this.data?.title || this.data?.resourceType || 'resource')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `${safeTitle || 'resource'}-qr.png`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }
}
